import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiActivity,
  FiBarChart2,
  FiClock,
  FiEye,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiUserCheck,
  FiUsers,
} from "react-icons/fi";

import Footer from "../../../components/Footer/Footer";
import { LoadingButton } from "../../../components/loadingButtom";
import { ModalDadosPessoaisCliente } from "../../../components/modais/ModalDadosPessoaisCliente";
import { ModalDesconectar } from "../../../components/modais/ModalDesconectar";
import { ModalHistoricoFinanceiro } from "../../../components/modais/ModalHistoricoFinanceiro";
import Navbar from "../../../components/Navbar/Navbar";
import { obterClientes, Cliente } from "./functions";
import "./ListarUsuarios.css";

type ServicoCliente = "Assessoria" | "Private";
type FiltroServico = "Todos" | ServicoCliente;
type ClienteComServico = Cliente & { servico: ServicoCliente };

const filtrosServico: FiltroServico[] = ["Todos", "Assessoria", "Private"];

const normalizarTexto = (valor?: string) =>
  (valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const obterIniciais = (nome?: string) => {
  const partes = (nome || "Cliente")
    .trim()
    .split(" ")
    .filter(Boolean)
    .slice(0, 2);

  return partes.map((parte) => parte[0]).join("").toUpperCase() || "CL";
};

const formatarAtualizacao = (data?: Date) => {
  if (!data) {
    return "Aguardando atualização";
  }

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
  }).format(data);
};

const ListarUsuarios = () => {
  const [modalDadosPessoais, setModalDadosPessoais] = useState<boolean>(false);
  const [listaClientes, setListaClientes] = useState<Cliente[]>([]);
  const [listaPrivates, setListaPrivates] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [erro, setErro] = useState<boolean>(false);
  const [desconectar, setDesconectar] = useState<boolean>(false);
  const [modalHistoricoFinanceiro, setModalHistoricoFinanceiro] = useState<boolean>(false);
  const [idCliente, setIdCliente] = useState<string>("");
  const [nomeCliente, setNomeCliente] = useState<string>("");
  const [reload, setReload] = useState<boolean>(false);
  const [busca, setBusca] = useState<string>("");
  const [filtroServico, setFiltroServico] = useState<FiltroServico>("Todos");
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date>();

  useEffect(() => {
    let ativo = true;

    const carregarClientes = async () => {
      await obterClientes(
        setListaClientes,
        setListaPrivates,
        setLoading,
        setErro,
        setDesconectar
      );

      if (ativo) {
        setUltimaAtualizacao(new Date());
      }
    };

    carregarClientes();

    return () => {
      ativo = false;
    };
  }, [reload]);

  const clientes = useMemo<ClienteComServico[]>(() => {
    const idsPrivate = new Set(listaPrivates.map((cliente) => cliente.idCredenciais));
    const nomesPrivate = new Set(listaPrivates.map((cliente) => normalizarTexto(cliente.nome)));

    return listaClientes.map((cliente) => ({
      ...cliente,
      servico:
        idsPrivate.has(cliente.idCredenciais) || nomesPrivate.has(normalizarTexto(cliente.nome))
          ? "Private"
          : "Assessoria",
    }));
  }, [listaClientes, listaPrivates]);

  const clientesFiltrados = useMemo(() => {
    const termo = normalizarTexto(busca);

    return clientes.filter((cliente) => {
      const correspondeServico =
        filtroServico === "Todos" || cliente.servico === filtroServico;
      const correspondeBusca =
        !termo ||
        normalizarTexto(cliente.nome).includes(termo) ||
        normalizarTexto(cliente.email).includes(termo);

      return correspondeServico && correspondeBusca;
    });
  }, [busca, clientes, filtroServico]);

  const totalPrivate = clientes.filter((cliente) => cliente.servico === "Private").length;
  const totalAssessoria = clientes.length - totalPrivate;

  const abrirDadosPessoais = (cliente: ClienteComServico) => {
    setIdCliente(cliente.idCredenciais);
    setModalDadosPessoais(true);
  };

  const abrirHistoricoFinanceiro = (cliente: ClienteComServico) => {
    setIdCliente(cliente.idCredenciais);
    setNomeCliente(cliente.nome);
    setModalHistoricoFinanceiro(true);
  };

  const recarregar = () => setReload((valorAtual) => !valorAtual);

  const renderizarAcoes = (cliente: ClienteComServico) => (
    <div className="listar-usuarios-actions" aria-label={`Ações de ${cliente.nome}`}>
      <button
        type="button"
        aria-label={`Ver dados pessoais de ${cliente.nome}`}
        title="Dados pessoais"
        onClick={() => abrirDadosPessoais(cliente)}
      >
        <FiEye />
      </button>
      <button
        type="button"
        aria-label={`Abrir histórico financeiro de ${cliente.nome}`}
        title="Histórico financeiro"
        onClick={() => abrirHistoricoFinanceiro(cliente)}
      >
        <FiClock />
      </button>
      <Link
        aria-label={`Abrir dashboards de ${cliente.nome}`}
        title="Dashboards"
        to={`/usuarios/dashboards/${cliente.idCredenciais}/${encodeURIComponent(cliente.nome)}`}
      >
        <FiBarChart2 />
      </Link>
    </div>
  );

  return (
    <>
      <Navbar />

      <main className="listar-usuarios-page">
        <section className="listar-usuarios-hero">
          <div className="listar-usuarios-hero-copy">
            <span>Gestão de clientes</span>
            <h1>Usuários</h1>
            <p>
              Consulte a base de clientes, abra dados cadastrais, acompanhe
              histórico financeiro e navegue para dashboards individuais.
            </p>
          </div>

          <div className="listar-usuarios-metrics" aria-label="Resumo de clientes">
            <div>
              <FiUsers />
              <span>Total</span>
              <strong>{clientes.length}</strong>
            </div>
            <div>
              <FiUserCheck />
              <span>Assessoria</span>
              <strong>{totalAssessoria}</strong>
            </div>
            <div>
              <FiShield />
              <span>Private</span>
              <strong>{totalPrivate}</strong>
            </div>
          </div>
        </section>

        <section className="listar-usuarios-toolbar" aria-label="Filtros de usuários">
          <label className="listar-usuarios-search">
            <FiSearch aria-hidden="true" />
            <input
              type="search"
              value={busca}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setBusca(event.target.value)
              }
              placeholder="Buscar por nome ou e-mail"
            />
          </label>

          <div className="listar-usuarios-filter" role="group" aria-label="Filtrar por serviço">
            {filtrosServico.map((filtro) => (
              <button
                key={filtro}
                type="button"
                className={filtroServico === filtro ? "active" : ""}
                onClick={() => setFiltroServico(filtro)}
              >
                {filtro}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="listar-usuarios-refresh"
            onClick={recarregar}
            disabled={loading}
          >
            <FiRefreshCw />
            Atualizar
          </button>
        </section>

        <section className="listar-usuarios-content">
          <div className="listar-usuarios-content-header">
            <div>
              <span>Base</span>
              <h2>Clientes cadastrados</h2>
            </div>
            <p>
              <FiActivity />
              {clientesFiltrados.length} de {clientes.length} exibidos ·{" "}
              {formatarAtualizacao(ultimaAtualizacao)}
            </p>
          </div>

          {loading ? (
            <div className="listar-usuarios-state">
              <LoadingButton />
            </div>
          ) : erro ? (
            <div className="listar-usuarios-state error">
              <strong>Não foi possível carregar os usuários.</strong>
              <button type="button" onClick={recarregar}>
                <FiRefreshCw />
                Tentar novamente
              </button>
            </div>
          ) : clientesFiltrados.length === 0 ? (
            <div className="listar-usuarios-state">
              <strong>Nenhum usuário encontrado.</strong>
            </div>
          ) : (
            <>
              <div className="listar-usuarios-table-wrap">
                <table className="listar-usuarios-table">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>E-mail</th>
                      <th>Serviço</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientesFiltrados.map((cliente) => (
                      <tr key={cliente.idCredenciais}>
                        <td>
                          <div className="listar-usuarios-client">
                            <span>{obterIniciais(cliente.nome)}</span>
                            <strong>{cliente.nome || "Cliente sem nome"}</strong>
                          </div>
                        </td>
                        <td>{cliente.email || "-"}</td>
                        <td>
                          <span className={`listar-usuarios-badge ${cliente.servico.toLowerCase()}`}>
                            {cliente.servico}
                          </span>
                        </td>
                        <td>{renderizarAcoes(cliente)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="listar-usuarios-mobile-list">
                {clientesFiltrados.map((cliente) => (
                  <article className="listar-usuarios-mobile-card" key={cliente.idCredenciais}>
                    <div className="listar-usuarios-mobile-card-header">
                      <span>{obterIniciais(cliente.nome)}</span>
                      <div>
                        <strong>{cliente.nome || "Cliente sem nome"}</strong>
                        <small>{cliente.email || "-"}</small>
                      </div>
                    </div>
                    <div className="listar-usuarios-mobile-card-footer">
                      <span className={`listar-usuarios-badge ${cliente.servico.toLowerCase()}`}>
                        {cliente.servico}
                      </span>
                      {renderizarAcoes(cliente)}
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </section>
      </main>

      {modalDadosPessoais && (
        <ModalDadosPessoaisCliente
          modal={modalDadosPessoais}
          setModal={() => setModalDadosPessoais(false)}
          idCliente={idCliente}
        />
      )}

      {modalHistoricoFinanceiro && (
        <ModalHistoricoFinanceiro
          modal={modalHistoricoFinanceiro}
          setModal={() => setModalHistoricoFinanceiro(false)}
          idCliente={idCliente}
          nomeCliente={nomeCliente}
        />
      )}

      {desconectar && <ModalDesconectar modal={desconectar} />}
      <Footer />
    </>
  );
};

export default ListarUsuarios;
